import React, { useState, useEffect } from 'react';
import { progressService, JourneyProgress, ProgressItem } from '../services/progressService';
import styles from '../styles/journeyOverview.module.css';

interface JourneyOverviewProps {
  user?: any;
  stepProgress?: Record<number, ProgressItem | null>;
}

const JourneyOverview: React.FC<JourneyOverviewProps> = ({ user, stepProgress }) => {
  const [journeyData, setJourneyData] = useState<JourneyProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadJourneyData();
    }
  }, [user]);

  const loadJourneyData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await progressService.getJourneyProgress();
      setJourneyData(data);
    } catch (err) {
      console.error('Error loading journey data:', err);
      setError('Failed to load journey data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.loading}>Loading journey data...</div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!journeyData) {
    return null;
  }

  if (journeyData.opted_out) {
    return null;
  }

  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    if (!user || !stepProgress) return 'not-started';
    
    const progress = stepProgress[stepNumber];
    if (!progress) return 'not-started';
    
    if (progress.completed) return 'completed';
    
    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }
    
    return 'not-started';
  };

  const gettingStartedStatus = getStepStatus(0);

  const getHighestWorkedStep = (): { stepNumber: number; status: 'completed' | 'in-progress' } | null => {
    if (!stepProgress) return null;
    
    let highestStep = 0;
    let highestStatus: 'completed' | 'in-progress' = 'not-started' as any;
    
    for (let step = 1; step <= 12; step++) {
      const status = getStepStatus(step);
      if (status === 'completed' || status === 'in-progress') {
        highestStep = step;
        highestStatus = status;
      }
    }
    
    return highestStep > 0 ? { stepNumber: highestStep, status: highestStatus } : null;
  };

  const allStepsCompleted = () => {
    if (!stepProgress) return false;
    for (let step = 1; step <= 12; step++) {
      if (getStepStatus(step) !== 'completed') return false;
    }
    return true;
  };

  const anyStepsStarted = () => {
    if (!stepProgress) return false;
    for (let step = 1; step <= 12; step++) {
      if (getStepStatus(step) !== 'not-started') return true;
    }
    return false;
  };

  const anyGettingStartedStarted = gettingStartedStatus !== 'not-started';

  if (allStepsCompleted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          Wow! Congratulations for working through all 12 steps of SAA!
        </div>
      </div>
    );
  }

  if (!anyGettingStartedStarted && !anyStepsStarted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.encouragingMessage}>
          You can do this! Start with Getting Started below, then begin Step One.
        </div>
      </div>
    );
  }

  if (gettingStartedStatus !== 'completed' && !anyStepsStarted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          {gettingStartedStatus === 'in-progress'
            ? 'Keep going with Getting Started — then begin Step One.'
            : 'Start with Getting Started before Step One.'}
        </div>
      </div>
    );
  }

  if (!anyStepsStarted() && gettingStartedStatus === 'completed') {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.encouragingMessage}>
          Great work on Getting Started! Click on Step 1 below to begin working the steps.
        </div>
      </div>
    );
  }

  const highestWorked = getHighestWorkedStep();
  if (highestWorked) {
    if (highestWorked.status === 'completed') {
      if (highestWorked.stepNumber === 12) {
        const incompleteSteps: number[] = [];
        for (let step = 1; step <= 12; step++) {
          if (getStepStatus(step) !== 'completed') {
            incompleteSteps.push(step);
          }
        }
        
        if (incompleteSteps.length > 0) {
          return (
            <div className={styles.journeyOverview}>
              <div className={styles.journeyMessage}>
                Congratulations on completing Step 12! You&apos;ve made great progress. To complete all 12 steps, continue working on the steps you haven&apos;t finished yet.
              </div>
            </div>
          );
        }
        return (
          <div className={styles.journeyOverview}>
            <div className={styles.journeyMessage}>
              Wow! Congratulations for working through all 12 steps of SAA!
            </div>
          </div>
        );
      }
      const nextStep = highestWorked.stepNumber + 1;
      return (
        <div className={styles.journeyOverview}>
          <div className={styles.journeyMessage}>
            Congratulations on completing Step {highestWorked.stepNumber}! Keep going. Click on Step {nextStep} below to continue your progress
          </div>
        </div>
      );
    }
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          You are making great progress - keep going! Click on Step {highestWorked.stepNumber} to continue your efforts
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.journeyOverview}>
      <div className={styles.journeyMessage}>
        You have started working the 12 steps. Keep going!
      </div>
    </div>
  );
};

export default JourneyOverview;
